import React from 'react';
import { Users } from 'lucide-react';

const UserGrowthChart = ({ userAnalytics = {} }) => {
  // Use available data from backend
  const totalUsers = userAnalytics.total || 0;
  const newUsers = userAnalytics.total || 0;
  const newClients = userAnalytics.new_clients || 0;
  const newVendors = userAnalytics.new_vendors || 0;
  const activeUsers = userAnalytics.active || 0;
  const verifiedUsers = userAnalytics.verified || 0;
  
  // Create simple growth data from available stats
  const growthData = [
    { period: 'Clients', count: newClients },
    { period: 'Vendors', count: newVendors },
    { period: 'Active', count: activeUsers },
    { period: 'Verified', count: verifiedUsers },
    { period: 'Total', count: totalUsers }
  ].filter(item => item.count > 0);

  // Always render the component, even with empty data
  if (growthData.length === 0 && totalUsers === 0) {
    return (
      <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">User Growth</h3>
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">No growth data available</p>
        </div>
      </div>
    );
  }

  const maxCount = Math.max(...growthData.map(d => Number(d.count || 0)), 1);

  return (
    <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">User Growth</h3>
        <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
      </div>
      <div className="space-y-3 mb-4">
        {growthData.slice(-5).map((item, index) => {
          const count = Number(item.count || 0);
          const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
          return (
            <div key={index} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400 font-medium">{item.period}</span>
                <span className="text-gray-900 dark:text-gray-50 font-semibold">{count} users</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                <div
                  className="bg-gradient-to-r from-indigo-500 to-indigo-600 h-2.5 rounded-full transition-all duration-500"
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className={`grid gap-4 ${(newClients > 0 || newVendors > 0) ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2'}`}>
          <div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Total Users</p>
            <p className="text-lg font-bold text-gray-900 dark:text-gray-50">{totalUsers}</p>
          </div>
          <div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">New This Period</p>
            <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{newUsers}</p>
          </div>
          {newClients > 0 && (
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">New Clients</p>
              <p className="text-lg font-bold text-sky-600 dark:text-sky-400">{newClients}</p>
            </div>
          )}
          {newVendors > 0 && (
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">New Vendors</p>
              <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{newVendors}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserGrowthChart;
